import { ReactNode } from 'react';
import { FeatureFlag } from '@studio/feature-flag-types';
import { EntitlementName } from 'studio5/types/entitlements.types';

/**
 * Describes the integration flow direction, for example, if we're sending or receiving data (or both).
 */
export const enum IntegrationDataFlow {
  /**
   * Data flows from the external system of record in Appcues
   */
  INBOUND = 'INBOUND',
  /**
   * Data flows from Appcues to the external system of record
   */
  OUTBOUND = 'OUTBOUND',
  /**
   * Data can flow in either or both directions
   */
  TWO_WAY = 'TWO_WAY',
}

/**
 * Describes the type of the integration. At Appcues, we have different categories of integrations
 * that need to be differentiated for the CTAs and how the integration instances are configured.
 */
export const enum IntegrationType {
  /**
   * This class of integrations is for when Appcues owns a deep integration implementation (likely with an
   * embedded experience like Prismatic’s integration platform). To support multiple integration platforms,
   * if this is selected, we must also specify the vendor type so we know how to trigger the
   * configuration and set up the integration.
   */
  THIRD_PARTY_VENDOR = 'THIRD_PARTY_VENDOR',
  /**
   * Integrations that are integrated on the Appcues JavaScript client passing through events and calls
   * in our SDK to a third party JavaScript SDK.
   */
  APPCUES_JS_SDK = 'APPCUES_JS_SDK',
  /**
   * Integrations where a external service (like Zapier, or our own custom webhooks) can be used
   * to get data into or out of Appcues.
   */
  DOCUMENTATION_ONLY = 'DOCUMENTATION_ONLY',
}

/* Third-party integration vendors */
export const enum IntegrationVendor {
  /**
   * Prismatic powered integrations
   */
  PRISMATIC = 'PRISMATIC',
}

type BaseIntegration = {
  /**
   * A unique id used to refer to the integration.
   */
  id: string;
  /**
   * Whether or not the customer must subscribe to gain access to this feature.
   * If this is true then the `entitlement` must be set.
   */
  isPremium: boolean;
  /**
   * The entitlement required to access this integration.
   */
  entitlement?: EntitlementName;
  /**
   * The Launch Darkly feature flag used to determine if the user should
   * see the integration in the listing page.
   * By default the integration will be visible if no rollout feature flag is set.
   */
  rolloutFeatureFlag?: FeatureFlag;
  /**
   * The ID of the integration that supersedes (replaces) this one.
   * If set and this integration is not connected, it will be hidden
   * in favor of the newer version.
   */
  supersededBy?: string;

  /**
   * The human-readable name displayed to the user.
   */
  name: string;
  /**
   * The URL to the documentation for this integration.
   */
  documentationUrl: string;
  /**
   * Short description of the purpose of this integration
   */
  shortDescription: ReactNode;
  /**
   * Short description of the purpose of this integration
   */
  longDescription: string | null;
  /**
   * The asset URL where the logo is served.
   */
  logoAssetUrl: string;
  integrationType: IntegrationType;
  dataFlowDirection: IntegrationDataFlow;
  integrationVendor?: IntegrationVendor;
};

export type ThirdPartyIntegration = BaseIntegration & {
  integrationType: IntegrationType.THIRD_PARTY_VENDOR;
  integrationVendor: IntegrationVendor;
};

export type DocumentationOnlyIntegration = BaseIntegration & {
  integrationType: IntegrationType.DOCUMENTATION_ONLY;
  documentationUrl: string;
};

export type ClientSideIntegration = BaseIntegration & {
  integrationType: IntegrationType.APPCUES_JS_SDK;
};

/**
 * Integration schema definition based on metadata information
 * Properties are required dinamically according to the metadata values.
 */
export type Integration =
  | ThirdPartyIntegration
  | DocumentationOnlyIntegration
  | ClientSideIntegration;
